/* ============================================================================
 * cabinet-visualizer.js — UI layer for the RTA cabinet color visualizer.
 *
 * Pairs with the Design handoff engine (cabinet-recolor.js) and palette
 * (sw-colors.js). This file builds the picker UI, mounts the live recolor on
 * the page's cabinet photos, persists the choice, writes it to the cart, and
 * (on product pages) injects the line-item properties into the add-to-cart form.
 *
 * It is intentionally framework-free and defensive: every integration point that
 * depends on the specific Porto theme markup is a CONFIG selector you can tune
 * from the section settings, and anything it can't find it simply skips (the
 * original photo stays visible — nothing breaks).
 * ==========================================================================*/
(function () {
  'use strict';

  if (!window.CabinetRecolor) {
    console.warn('[cabinet-visualizer] cabinet-recolor.js not loaded; aborting.');
    return;
  }

  var CR = window.CabinetRecolor;
  var COLORS = (window.SW_COLORS || []).slice();
  var BOX = CR.BOX_BEIGE;

  // ---- read config injected by the Liquid section --------------------------
  var CFG = window.RTA_VIZ_CONFIG || {};
  var GRID_SELECTOR = CFG.gridSelector || '';        // collection grid <img> selector (optional)
  // Product-page gallery selectors. The configured selector (if any) is tried
  // first, then known Porto 4 / Dawn gallery markup as fallback so every slide
  // and thumbnail mounts even if the saved section setting targets one element.
  var PRODUCT_FALLBACKS = [
    '.product-single__photos img',
    '.product-single__photo img',
    '.product-single__thumbnails img',
    '.product-images img',
    '.product-gallery img',
    '.product__media img'
  ];
  var PRODUCT_SELECTOR = [CFG.productSelector || ''].concat(PRODUCT_FALLBACKS)
    .filter(Boolean).join(', ');
  var DEFAULT_HEX = CFG.defaultHex || null;          // null => leave photos as-is until a pick

  // ---- hue bucketing (works at any list size, no manual family tags) --------
  var BUCKETS = ['Whites & Neutrals', 'Reds', 'Oranges & Browns', 'Yellows & Golds', 'Greens', 'Blues'];

  function hexToHsl(hex) {
    var r = parseInt(hex.slice(1, 3), 16) / 255,
        g = parseInt(hex.slice(3, 5), 16) / 255,
        b = parseInt(hex.slice(5, 7), 16) / 255;
    var mx = Math.max(r, g, b), mn = Math.min(r, g, b), d = mx - mn;
    var l = (mx + mn) / 2, s = 0, h = 0;
    if (d) {
      s = d / (1 - Math.abs(2 * l - 1));
      if (mx === r) h = ((g - b) / d) % 6;
      else if (mx === g) h = (b - r) / d + 2;
      else h = (r - g) / d + 4;
      h *= 60; if (h < 0) h += 360;
    }
    return { h: h, s: s, l: l };
  }

  function bucketOf(hex) {
    var c = hexToHsl(hex);
    if (c.s < 0.14 || c.l > 0.92 || c.l < 0.08) return 'Whites & Neutrals';
    var h = c.h;
    if (h < 15 || h >= 345) return 'Reds';
    if (h < 45) return 'Oranges & Browns';
    if (h < 70) return 'Yellows & Golds';
    if (h < 170) return 'Greens';
    if (h < 260) return 'Blues';
    return 'Purples & Pinks';
  }

  // ---- recolor instances on this page --------------------------------------
  var instances = [];
  window.__rtaInstances = instances; // shared with the product-page bar (snapshot)

  function mountImage(imgEl) {
    if (!imgEl || imgEl.dataset.rtaMounted) return;
    if (CR.shouldSkip(imgEl)) { imgEl.dataset.rtaMounted = 'skip'; return; }

    // Lazy-loaded images (Porto sets src only when the slide/thumb loads):
    // wait for the real source, then mount.
    var src = imgEl.currentSrc || imgEl.src;
    if (!src || src.indexOf('data:') === 0) {
      imgEl.addEventListener('load', function onLoad() {
        imgEl.removeEventListener('load', onLoad);
        mountImage(imgEl);
      });
      return;
    }
    imgEl.dataset.rtaMounted = '1';

    // Recolor by swapping the image source (Dawn/OS-2.0 friendly: no DOM
    // restructuring, plays nice with srcset/<picture>/sliders). The original
    // <img> stays in the DOM for SEO/LCP; we only change what it points to.
    imgEl.dataset.rtaBase = src;

    var base = new Image();
    base.crossOrigin = 'anonymous';
    base.onload = function () {
      if (imgEl.dataset.rtaBase !== src) return; // remounted meanwhile — stale
      var canvas = document.createElement('canvas'); // offscreen
      var inst = new CR.Instance(base, canvas, {
        onReady: function (ins) {                 // mask loaded + segmented
          var sel = current();
          if (sel) applyOne(ins, sel.hex);
        }
      });
      inst._canvasEl = canvas;   // used by the product-page snapshot
      inst._imgEl = imgEl;
      instances.push(inst);
      var sel = current();
      if (sel) applyOne(inst, sel.hex);
    };
    base.onerror = function () { /* leave original <img> visible */ };
    base.src = src;
  }

  function applyOne(inst, hex) {
    inst.setColor(hex);                            // queues if mask not ready yet
    if (inst.disabled || !inst.ready) return;      // no mask => original photo stays
    var imgEl = inst._imgEl;
    if (!imgEl) return;
    // Neutralize responsive sources so our recolored image wins.
    if (imgEl.parentNode && imgEl.parentNode.tagName === 'PICTURE') {
      var sources = imgEl.parentNode.querySelectorAll('source');
      for (var i = 0; i < sources.length; i++) sources[i].setAttribute('srcset', '');
    }
    imgEl.removeAttribute('srcset');
    imgEl.removeAttribute('data-srcset');
    imgEl.removeAttribute('sizes');
    // Keep the photo's true proportions regardless of the theme's width/height
    // attributes or fixed-ratio media container (prevents stretch/elongation).
    imgEl.removeAttribute('width');
    imgEl.removeAttribute('height');
    if (inst.seg) imgEl.style.aspectRatio = inst.seg.W + ' / ' + inst.seg.H;
    imgEl.style.height = 'auto';
    imgEl.style.objectFit = 'contain';
    imgEl.src = inst.snapshotDataURL();
  }

  function applyAll(hex) {
    for (var i = 0; i < instances.length; i++) applyOne(instances[i], hex);
  }

  function mountAll() {
    if (PRODUCT_SELECTOR) qsa(PRODUCT_SELECTOR).forEach(mountImage);
    // explicit opt-in images (e.g. the section's own sticky preview)
    qsa('img[data-recolor]').forEach(mountImage);
    if (GRID_SELECTOR) lazyGrid(GRID_SELECTOR);
  }

  function isCandidate(el) {
    if (!el || el.tagName !== 'IMG') return false;
    if (el.hasAttribute('data-recolor')) return true;
    try {
      if (PRODUCT_SELECTOR && el.matches(PRODUCT_SELECTOR)) return true;
      if (GRID_SELECTOR && el.matches(GRID_SELECTOR)) return true;
    } catch (e) {}
    return false;
  }

  function remountImage(imgEl) {
    // Drop the stale instance for this element, then mount fresh.
    for (var i = instances.length - 1; i >= 0; i--) {
      if (instances[i]._imgEl === imgEl) instances.splice(i, 1);
    }
    delete imgEl.dataset.rtaMounted;
    delete imgEl.dataset.rtaBase;
    mountImage(imgEl);
  }

  // Porto's gallery lazy-loads slides, clones them for the carousel, and swaps
  // the main image src on thumbnail clicks — all after boot. Watch the DOM and
  // (re)mount anything that appears or changes, so every gallery image stays
  // recolored. Our own writes are data: URLs and are ignored (no loops).
  function observeDynamicImages() {
    if (!('MutationObserver' in window)) return;
    var mo = new MutationObserver(function (muts) {
      for (var m = 0; m < muts.length; m++) {
        var mu = muts[m];
        if (mu.type === 'childList') {
          for (var a = 0; a < mu.addedNodes.length; a++) {
            var node = mu.addedNodes[a];
            if (node.nodeType !== 1) continue;
            if (isCandidate(node)) mountImage(node);
            else if (node.querySelectorAll) {
              var imgs = node.querySelectorAll('img');
              for (var k = 0; k < imgs.length; k++) if (isCandidate(imgs[k])) mountImage(imgs[k]);
            }
          }
        } else if (mu.type === 'attributes' && mu.attributeName === 'src') {
          var img = mu.target;
          if (!isCandidate(img)) continue;
          var s = img.src || '';
          if (s.indexOf('data:') === 0) continue;            // our own write
          if (!img.dataset.rtaMounted) { mountImage(img); continue; }
          if (img.dataset.rtaMounted === 'skip') continue;
          if (s === img.dataset.rtaBase) {
            // Theme reverted to the original photo — re-apply the recolor.
            var sel = current();
            if (sel) {
              for (var j = 0; j < instances.length; j++) {
                if (instances[j]._imgEl === img) { applyOne(instances[j], sel.hex); break; }
              }
            }
          } else {
            // Theme swapped to a different photo (thumbnail click) — rebuild.
            remountImage(img);
          }
        }
      }
    });
    mo.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['src'] });
  }

  // ---- collection grid: viewport-only + cached -----------------------------
  function lazyGrid(selector) {
    if (!('IntersectionObserver' in window)) { qsa(selector).forEach(mountImage); return; }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        mountImage(e.target);
        io.unobserve(e.target);
      });
    }, { rootMargin: '300px' });
    qsa(selector).forEach(function (img) { io.observe(img); });
  }

  // ---- selection state ------------------------------------------------------
  function current() { return CR.load(); }

  function choose(sw) {
    applyAll(sw.hex);
    CR.save(sw);
    CR.saveToCart(sw);
    renderBar(sw);
    injectCartProps(sw);
    qsa('.rta-row[aria-selected="true"]').forEach(function (el) { el.setAttribute('aria-selected', 'false'); });
    var row = document.querySelector('.rta-row[data-code="' + cssEsc(sw.code) + '"]');
    if (row) row.setAttribute('aria-selected', 'true');
  }
  window.RTA_chooseColor = choose; // exposed for inline handlers / debugging

  // ---- "Your selection" bar -------------------------------------------------
  function renderBar(sw) {
    var bar = document.getElementById('rta-selection-bar');
    if (!bar) return;
    if (!sw) { bar.hidden = true; return; }
    bar.hidden = false;
    bar.querySelector('.rta-bar-sw').style.background = sw.hex;
    bar.querySelector('.rta-bar-name').textContent = sw.name;
    bar.querySelector('.rta-bar-code').textContent = sw.code;
  }

  // ---- cart line-item property injection (product pages) --------------------
  // Adds hidden inputs to the product form so the order carries the color/note.
  function injectCartProps(sw) {
    var forms = qsa('form[action*="/cart/add"]');
    forms.forEach(function (form) {
      setProp(form, 'Front color', sw.name + ' ' + sw.code);
      var note = document.getElementById('rta-note');
      if (note) setProp(form, 'Note', (note.value || '').slice(0, 100).trim());
      // Preview image is added at submit time (see snapshot handler) once hosted.
    });
  }
  function setProp(form, key, value) {
    var name = 'properties[' + key + ']';
    var input = form.querySelector('input[name="' + cssEsc(name) + '"]');
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      form.appendChild(input);
    }
    input.value = value;
  }

  // ---- UI build -------------------------------------------------------------
  function buildUI() {
    var root = document.getElementById('rta-color-picker');
    if (!root) return;

    // search fields
    var byName = root.querySelector('#rta-search-name');
    var byNum = root.querySelector('#rta-search-num');
    var listEl = root.querySelector('#rta-color-list');
    var popularEl = root.querySelector('#rta-popular');

    // popular picks (first ~10; replace RECENT hook with live order data later)
    var popular = (window.RTA_RECENT && window.RTA_RECENT.length ? window.RTA_RECENT : COLORS.slice(0, 10));
    if (popularEl) {
      popular.slice(0, 12).forEach(function (sw) { popularEl.appendChild(swatchChip(sw)); });
    }

    // ---- browse-by-collection filter chips ----
    var collections = window.SW_COLLECTIONS || {};
    var collNames = Object.keys(collections);
    var activeCollection = null;
    if (collNames.length && listEl) {
      injectCollStyles();
      var bar = document.createElement('div');
      bar.className = 'rta-coll-bar';
      bar.appendChild(collChip('All Colors', null, true));
      collNames.forEach(function (nm) { bar.appendChild(collChip(nm, nm, false)); });
      listEl.parentNode.insertBefore(bar, listEl);
    }
    function collChip(label, key, pressed) {
      var b = document.createElement('button');
      b.type = 'button'; b.className = 'rta-coll-chip'; b.textContent = label;
      b.setAttribute('aria-pressed', pressed ? 'true' : 'false');
      b.addEventListener('click', function () {
        activeCollection = key;
        var chips = document.querySelectorAll('.rta-coll-chip');
        for (var i = 0; i < chips.length; i++) chips[i].setAttribute('aria-pressed', 'false');
        b.setAttribute('aria-pressed', 'true');
        render();
      });
      return b;
    }

    function render() {
      var nameq = (byName && byName.value || '').trim().toLowerCase();
      var numq = (byNum && byNum.value || '').replace(/sw|\s|#/gi, '').toLowerCase();
      var searching = nameq || numq;

      var base = COLORS;
      if (activeCollection && collections[activeCollection]) {
        var inColl = {};
        collections[activeCollection].forEach(function (c) { inColl[c.code] = 1; });
        base = COLORS.filter(function (c) { return inColl[c.code]; });
      }
      var filtered = base.filter(function (sw) {
        var okName = !nameq || sw.name.toLowerCase().indexOf(nameq) !== -1;
        var okNum = !numq || sw.code.replace(/sw|\s|#/gi, '').toLowerCase().indexOf(numq) !== -1;
        return okName && okNum;
      });

      // Lead with the Top 50 group only on the default "All Colors" view (not searching, no collection filter)
      var leadTop = !searching && !activeCollection && popular.length;
      var popCodes = {};
      if (leadTop) for (var pi = 0; pi < popular.length; pi++) popCodes[popular[pi].code] = 1;

      // group by hue bucket
      var groups = {};
      filtered.forEach(function (sw) {
        if (leadTop && popCodes[sw.code]) return;   // already shown in Top 50 lead group
        var b = bucketOf(sw.hex);
        (groups[b] = groups[b] || []).push(sw);
      });

      listEl.innerHTML = '';

      if (leadTop) {
        listEl.appendChild(groupBlock('Top 50 Colors', popular));
      }
      BUCKETS.forEach(function (b) {
        if (!groups[b] || !groups[b].length) return;
        listEl.appendChild(groupBlock(b, groups[b]));
      });
      if (!listEl.children.length) {
        var empty = document.createElement('p');
        empty.className = 'rta-empty';
        empty.textContent = 'No colors match that search.';
        listEl.appendChild(empty);
      }
    }

    if (byName) byName.addEventListener('input', render);
    if (byNum) byNum.addEventListener('input', render);
    render();
  }

  function groupBlock(title, list) {
    var wrap = document.createElement('div');
    wrap.className = 'rta-group';
    var h = document.createElement('h4');
    h.className = 'rta-group-title';
    h.textContent = title + ' (' + list.length + ')';
    wrap.appendChild(h);
    list.forEach(function (sw) { wrap.appendChild(colorRow(sw)); });
    return wrap;
  }

  function colorRow(sw) {
    var sel = current();
    var row = document.createElement('button');
    row.type = 'button';
    row.className = 'rta-row';
    row.dataset.code = sw.code;
    row.setAttribute('aria-selected', sel && sel.code === sw.code ? 'true' : 'false');
    row.innerHTML =
      '<span class="rta-sw" style="background:' + sw.hex + '"></span>' +
      '<span class="rta-row-name">' + esc(sw.name) + '</span>' +
      '<span class="rta-row-code">' + esc(sw.code) + '</span>' +
      '<span class="rta-check" aria-hidden="true">✓</span>';
    row.addEventListener('click', function () { choose(sw); });
    return row;
  }

  function swatchChip(sw) {
    var b = document.createElement('button');
    b.type = 'button';
    b.className = 'rta-chip';
    b.title = sw.name + ' ' + sw.code;
    b.style.background = sw.hex;
    b.addEventListener('click', function () { choose(sw); });
    return b;
  }

  // ---- helpers --------------------------------------------------------------
  function qsa(sel) { try { return Array.prototype.slice.call(document.querySelectorAll(sel)); } catch (e) { return []; } }
  function esc(s) { return String(s).replace(/[&<>"]/g, function (c) { return ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]; }); }
  function cssEsc(s) { return String(s).replace(/"/g, '\\"'); }
  function injectCollStyles() {
    if (document.getElementById('rta-coll-style')) return;
    var s = document.createElement('style');
    s.id = 'rta-coll-style';
    s.textContent = '.rta-coll-bar{display:flex;flex-wrap:wrap;gap:6px;margin:0 0 12px}' +
      '.rta-coll-chip{padding:6px 12px;border:1px solid #d9d4cb;border-radius:999px;background:#fff;cursor:pointer;font-size:13px}' +
      '.rta-coll-chip[aria-pressed="true"]{background:#1c1a17;color:#f3efe7;border-color:#1c1a17}';
    document.head.appendChild(s);
  }

  // ---- boot -----------------------------------------------------------------
  function boot() {
    mountAll();
    observeDynamicImages();
    buildUI();
    var sel = current();
    if (sel) { renderBar(sel); injectCartProps(sel); }
    else if (DEFAULT_HEX) { applyAll(DEFAULT_HEX); }
    // else: leave photos as-is (Snow White) until a deliberate pick — decision A.
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();